'use client';

import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const queryData = [
  { query: 'acme', impressions: 9840, clicks: 3120 },
  { query: 'acme.io', impressions: 5430, clicks: 1980 },
  { query: 'acme dash', impressions: 2870, clicks: 820 },
  { query: 'acme login', impressions: 2240, clicks: 710 },
  { query: 'acme price', impressions: 1680, clicks: 490 },
];

const CRIMSON = '#961932';
const CRIMSON_DARK = '#8C0B31';

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.12)_0px_8px_24px] p-3 min-w-[140px]">
      <p className="text-xs font-600 text-[#39364E] mb-2 font-mono">{label}</p>
      {payload.map((entry) => (
        <div key={`tq-${entry.name}`} className="flex items-center justify-between gap-3 text-xs mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-[#696969] capitalize">{entry.name}</span>
          </div>
          <span className="font-600 tabular-nums text-[#39364E]">{entry.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

export default function TopQueriesChart() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] p-5 h-full hover:shadow-[rgba(150,25,50,0.08)_0px_8px_24px] transition-shadow duration-200">
      <div className="flex items-center gap-2 mb-1">
        {/* Smarty-style icon wrapper: purple bg */}
        <div
          className="w-[38px] h-[38px] rounded-[5px] flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgb(240, 228, 251)' }}
        >
          <TrendingUp size={17} className="text-[#39364E]" />
        </div>
        <div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Top Queries</h2>
          <p className="text-xs text-[#696969]">Impressions vs Clicks</p>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2.5 rounded-sm bg-[#961932]" />
          <span className="text-[11px] text-[#696969]">Impressions</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2.5 rounded-sm bg-[#8C0B31]" />
          <span className="text-[11px] text-[#696969]">Clicks</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <BarChart
          data={queryData}
          margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          barGap={2}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <CartesianGrid stroke="#f0e8ea" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="query"
            tick={{ fontSize: 10, fill: '#696969', fontFamily: 'IBM Plex Mono, monospace' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#696969' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
            }
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(150,25,50,0.04)' }} />
          <Bar
            dataKey="impressions"
            radius={[3, 3, 0, 0]}
            onMouseEnter={(_, index) => setActiveIndex(index)}
          >
            {queryData.map((_, index) => (
              <Cell
                key={`imp-cell-${index}`}
                fill={activeIndex === index ? '#8C0B31' : CRIMSON}
                opacity={activeIndex !== null && activeIndex !== index ? 0.6 : 1}
              />
            ))}
          </Bar>
          <Bar
            dataKey="clicks"
            radius={[3, 3, 0, 0]}
            onMouseEnter={(_, index) => setActiveIndex(index)}
          >
            {queryData.map((_, index) => (
              <Cell
                key={`clk-cell-${index}`}
                fill={activeIndex === index ? '#961932' : CRIMSON_DARK}
                opacity={activeIndex !== null && activeIndex !== index ? 0.6 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}